import { useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { Logger } from '@/utils/logging';

import type {
  ApiError,
  UseVerifyPhoneMutationOptions,
  VerifyPhoneRequest,
  VerifyPhoneResponse,
} from '../useSignupMutation/types';

const verifyPhone = async (
  verificationData: VerifyPhoneRequest,
): Promise<VerifyPhoneResponse> => {
  try {
    const { data } = await apiClient.post<VerifyPhoneResponse>(
      '/v1/customer/verify-phone',
      verificationData,
    );
    return data;
  } catch (error: unknown) {
    Logger.error('Error verifying phone', { error });
    const err = error as Record<string, unknown>;
    throw {
      message: (err?.message as string) || 'Phone verification failed',
      code: (err?.code as string) || 'VERIFY_PHONE_ERROR',
      details: err?.details,
    } as ApiError;
  }
};

export const useVerifyPhoneMutation = (
  options?: UseVerifyPhoneMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyPhone,
    ...options,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['user'] });
      const previousUserData = queryClient.getQueryData(['user']);
      queryClient.setQueryData(['user'], (old: unknown) => ({
        ...(old as Record<string, unknown>),
        phoneVerified: true,
      }));
      return { previousUserData };
    },
    onSuccess: (data, variables, context, mutationContext) => {
      queryClient.invalidateQueries({
        queryKey: ['customer'],
      });

      options?.onSuccess?.(data, variables, context, mutationContext);
    },
    onError: (err, newVerificationData, context, mutationContext) => {
      if (context?.previousUserData) {
        queryClient.setQueryData(['user'], context.previousUserData);
      }
      options?.onError?.(err, newVerificationData, context, mutationContext);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    retry: (failureCount, error) => {
      // Retry on network errors but not on invalid code
      const apiError = error as ApiError;
      if (
        apiError.code === 'INVALID_CODE' ||
        apiError.code === 'CODE_EXPIRED'
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
};
